import React, { useState } from 'react'
import useSWR from 'swr'
import {
  fetchWidgets, createWidget, updateWidget, deleteWidget, fetchWidgetPlacements, Widget, WidgetPlacementSummary
} from '../../services/api'
import { WIDGET_TYPES, getWidgetTypeLabel } from '../../constants/widgetTypes'
import { useWidgetSite } from '../../contexts/WidgetSiteContext'
import { useToast } from '../../contexts/ToastContext'
import { Button, Badge, Banner, Panel, Drawer, useSpeedbump } from '../../components'
import { useWidgetFormState } from '../../hooks'
import WidgetForm from '../../components/merchandising/WidgetForm'
import WidgetTypeIcon from '../../components/merchandising/WidgetTypeIcon'
import pageStyles from '../../styles/page.module.scss'
import listTableStyles from '../../styles/list-table.module.scss'
import formStyles from '../../styles/forms.module.scss'
import styles from './WidgetLibrary.module.scss'

const WidgetLibrary: React.FC = () => {
  const { siteId, siteName, setSite } = useWidgetSite()
  const { toast } = useToast()
  const confirm = useSpeedbump()
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [editingWidget, setEditingWidget] = useState<Widget | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [saving, setSaving] = useState(false)

  const { data: widgetsData, mutate, error } = useSWR(
    siteId ? ['merch-widgets', siteId] : null,
    () => fetchWidgets(siteId),
    { revalidateOnFocus: false }
  )
  const widgets: Widget[] = widgetsData?.widgets || []

  const formState = useWidgetFormState({
    siteId,
    initialWidget: editingWidget,
    onWidgetsChanged: () => { void mutate() },
  })

  const filtered = widgets.filter(w => {
    if (typeFilter && w.widget_type !== typeFilter) return false
    if (searchTerm && !w.name.toLowerCase().includes(searchTerm.toLowerCase())) return false
    return true
  })

  // Fetch placements for the widget being edited
  const { data: widgetPlacementsData, isLoading: placementsLoading } = useSWR(
    formState.widgetId ? ['merch-widget-placements', formState.widgetId] : null,
    () => fetchWidgetPlacements(formState.widgetId!),
    { revalidateOnFocus: false }
  )
  const widgetPlacements: WidgetPlacementSummary[] = widgetPlacementsData?.placements || []

  const openCreate = () => {
    setEditingWidget(null)
    formState.reset(null)
    setShowCreateForm(true)
  }

  const openEdit = (w: Widget) => {
    setEditingWidget(w)
    formState.reset(w)
    setShowCreateForm(true)
  }

  const closeForm = () => {
    formState.discardIfAutoCreated()
    setShowCreateForm(false)
    setEditingWidget(null)
  }

  const handleSave = async () => {
    if (!formState.name.trim()) { toast('Widget name is required', 'error'); return }
    if (!formState.type) { toast('Widget type is required', 'error'); return }
    setSaving(true)
    try {
      if (formState.widgetId) {
        await updateWidget(formState.widgetId, {
          name: formState.name.trim(),
          widget_type: formState.type,
          metadata: formState.metadata as Record<string, unknown>,
        })
        toast(formState.autoCreated ? 'Widget created' : 'Widget updated')
      } else {
        await createWidget({
          site_id: siteId,
          widget_type: formState.type,
          name: formState.name.trim(),
          metadata: formState.metadata as Record<string, unknown>,
        })
        toast('Widget created')
      }
      formState.markCommitted()
      await mutate()
      setShowCreateForm(false)
      setEditingWidget(null)
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to save widget', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!formState.widgetId) return
    const ok = await confirm({
      title: 'Delete widget',
      confirmLabel: 'Delete',
      confirmVariant: 'danger',
      message: `Delete “${formState.name}”? This cannot be undone.`,
    })
    if (!ok) return
    try {
      await deleteWidget(formState.widgetId)
      formState.markCommitted() // prevent closeForm's discard path
      await mutate()
      setShowCreateForm(false)
      setEditingWidget(null)
      toast('Widget deleted')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete widget', 'error')
    }
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.headerButtons}>
        <Button variant="secondary" size="small" onClick={() => setSite('', '')}>
          Switch Site
        </Button>
      </div>
      <div className={pageStyles.pageHeader}>
        <h1>Widget Library — {siteName}</h1>
      </div>

      {error && (
        <Banner variant="error" message={`Unable to load widgets: ${error.message || 'Connection failed'}`} />
      )}

      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={listTableStyles.searchInputGroup}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search widgets by name..."
              className={listTableStyles.searchInput}
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} className={listTableStyles.clearButton}>
                Clear
              </button>
            )}
          </div>
        </div>
        <div className={styles.filterRow}>
          <div className={styles.typeFilters}>
            <button
              type="button"
              className={`${styles.typeFilter} ${!typeFilter ? styles.typeFilterActive : ''}`}
              onClick={() => setTypeFilter('')}
            >
              All ({widgets.length})
            </button>
            {WIDGET_TYPES.map(wt => {
              const count = widgets.filter(w => w.widget_type === wt.code).length
              return (
                <button
                  key={wt.code}
                  type="button"
                  className={`${styles.typeFilter} ${typeFilter === wt.code ? styles.typeFilterActive : ''}`}
                  onClick={() => setTypeFilter(wt.code)}
                >
                  {wt.label} ({count})
                </button>
              )
            })}
          </div>
          <Button size="small" onClick={openCreate}>+ New Widget</Button>
        </div>
      </div>

      {filtered.length === 0 && !error && (
        <div className={styles.emptyState}>
          {widgets.length === 0
            ? <p>No widgets yet. Create one to get started.</p>
            : <p>No widgets match your filters.</p>
          }
        </div>
      )}

      {filtered.length > 0 && (
        <div className={styles.widgetGrid}>
          {filtered.map(w => (
            <Panel key={w.id} variant="no-margin">
              <div className={styles.widgetCard}>
                <div className={styles.widgetCardHeader}>
                  <div className={styles.widgetCardTitle}>
                    <button type="button" className={styles.widgetNameBtn} onClick={() => openEdit(w)}>
                      {w.name}
                    </button>
                    <Badge variant="secondary">{getWidgetTypeLabel(w.widget_type)}</Badge>
                  </div>
                </div>
                <div className={styles.widgetCardPreview}>
                  {w.widget_type === 'video_embed' && w.metadata?.video_id ? (
                    <img
                      src={`https://cdn.jwplayer.com/v2/media/${w.metadata.video_id}/poster.jpg?width=480`}
                      alt={w.name}
                      className={styles.previewImage}
                    />
                  ) : w.widget_type === 'banner_image' && w.preview_url ? (
                    <img
                      src={w.preview_url}
                      alt={w.name}
                      className={styles.previewImage}
                    />
                  ) : (
                    <div className={styles.previewPlaceholder}>
                      <WidgetTypeIcon code={w.widget_type} size={40} className={styles.previewPlaceholderIcon} />
                      <span>{getWidgetTypeLabel(w.widget_type)}</span>
                    </div>
                  )}
                </div>
              </div>
            </Panel>
          ))}
        </div>
      )}

      {/* Create / Edit Drawer */}
      <Drawer
        isOpen={showCreateForm}
        onClose={closeForm}
        title={editingWidget ? `Edit: ${editingWidget.name}` : 'New Widget'}
        footer={formState.type ? (
          <>
            {editingWidget && (
              <Button
                variant="danger"
                size="small"
                onClick={handleDelete}
                disabled={widgetPlacements.length > 0 || placementsLoading}
                title={widgetPlacements.length > 0 ? 'Remove from all placements before deleting' : undefined}
              >
                Delete
              </Button>
            )}
            <div className={styles.footerRight}>
              <Button variant="secondary" onClick={closeForm}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving || formState.autoCreating}>
                {saving ? 'Saving...' : editingWidget ? 'Save Changes' : 'Create Widget'}
              </Button>
            </div>
          </>
        ) : undefined}
      >
        <WidgetForm state={formState} />

        {editingWidget && (
          <div className={formStyles.fieldGroup}>
            <label>Placements ({placementsLoading ? '...' : widgetPlacements.length})</label>
            {widgetPlacements.length === 0 && !placementsLoading && (
              <div className={styles.widgetMetaEmpty}>Not used by any placements</div>
            )}
            {widgetPlacements.length > 0 && (
              <ul className={styles.placementList}>
                {widgetPlacements.map(p => (
                  <li key={p.id}>
                    <span>{p.name}</span>
                    {(p.page || p.page_type) && (
                      <span className={styles.placementListMeta}> — {p.page ? p.page.name : p.page_type}</span>
                    )}
                    <Badge variant={p.is_currently_active ? 'success' : 'neutral'}>
                      {p.is_currently_active ? 'Active' : 'Upcoming'}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default WidgetLibrary
